import { getSectionHTML } from '../utils/section-renderer';
import appStore from '../utils/state';
import type { CartProductRef, CartState } from '../types';
import gsap from 'gsap';
import type ProductCarousel from './product-carousel';

const PER_PRODUCT_LIMIT = 6;
const FINAL_LIMIT = 6;
const INTENT = 'related';
const DEBOUNCE_MS = 400;

type RecommendationProduct = {
  id: number;
  handle: string;
  available?: boolean;
};

type RecommendationsResponse = {
  products?: RecommendationProduct[];
};

type ScoredProduct = {
  id: number;
  handle: string;
  score: number;
};

export default class CartRecommendedProducts extends HTMLElement {
  private grid: HTMLElement | null = null;
  private itemTemplate: HTMLTemplateElement | null = null;
  private fallbackHtml: string = '';
  private lastSourceIdsKey: string = '';
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private unsubscribeFromCart: (() => void) | null = null;
  private fadeTl: gsap.core.Timeline | null = null;
  private loadGeneration = 0;

  private onCartUpdatedBound = this.onCartUpdated.bind(this);

  connectedCallback(): void {
    this.grid = this.querySelector('[data-product-carousel-ref="grid"]');
    this.itemTemplate = this.querySelector('[data-cart-recommended-products-template="item"]');
    this.fallbackHtml = this.grid?.innerHTML ?? '';

    gsap.set(this.grid, { autoAlpha: this.fallbackHtml.trim() ? 1 : 0, visibility: 'visible' });

    this.unsubscribeFromCart = appStore.subscribeKey('cart', this.onCartUpdatedBound);
    void this.refreshRecommendations();
  }

  disconnectedCallback(): void {
    this.killFadeTl();

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    if (this.unsubscribeFromCart) {
      this.unsubscribeFromCart();
      this.unsubscribeFromCart = null;
    }
  }

  private get recommendationsUrl(): string | null {
    const url = this.dataset.recommendationsUrl?.trim();
    return url || null;
  }

  private get productCardSectionId(): string {
    return this.dataset.productCardSectionId || 'horizontal-product-card-ajax';
  }

  private get excludeHandles(): Set<string> {
    const raw = this.dataset.excludeHandles?.trim() ?? '';
    if (!raw) return new Set();

    return new Set(
      raw
        .split(',')
        .map((handle) => handle.trim())
        .filter(Boolean),
    );
  }

  private onCartUpdated(_cart: CartState): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      void this.refreshRecommendations();
    }, DEBOUNCE_MS);
  }

  private get cartDrawer(): HTMLElement | null {
    return this.closest('cart-drawer');
  }

  private getItemCount(): number {
    const cartData = appStore.getState().cart.data;
    if (cartData !== null) {
      return cartData.item_count;
    }

    return parseInt(this.cartDrawer?.dataset.quantity ?? '0', 10);
  }

  private getSourceProducts(): CartProductRef[] {
    const drawer = this.cartDrawer;
    if (!drawer) return [];

    const excludeHandles = this.excludeHandles;
    const products: CartProductRef[] = [];
    const seenIds = new Set<number>();

    for (const el of drawer.querySelectorAll('cart-item')) {
      const id = Number(el.getAttribute('data-product-id'));
      const handle = el.getAttribute('data-product-handle')?.trim();
      const isFreeGift = el.hasAttribute('data-free-gift');

      if (!id || !handle || isFreeGift || excludeHandles.has(handle)) continue;
      if (seenIds.has(id)) continue;

      seenIds.add(id);
      products.push({ id, handle });
    }

    return products;
  }

  private getSourceIdsKey(sources: CartProductRef[]): string {
    return sources
      .map((product) => product.id)
      .sort((a, b) => a - b)
      .join(',');
  }

  private killFadeTl(): void {
    if (this.fadeTl) {
      this.fadeTl.kill();
      this.fadeTl = null;
    }
  }

  private fadeOutGrid(): Promise<void> {
    if (!this.grid) return Promise.resolve();

    this.killFadeTl();

    return new Promise((resolve) => {
      this.fadeTl = gsap.timeline({ onComplete: resolve });
      this.fadeTl.to(this.grid, { autoAlpha: 0, duration: 0.2, ease: 'power2.out' });
    });
  }

  private fadeInGrid(): void {
    if (!this.grid) return;

    this.killFadeTl();
    gsap.set(this.grid, { visibility: 'visible' });
    this.fadeTl = gsap.timeline();
    this.fadeTl.fromTo(this.grid, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.3, ease: 'power2.out' });
  }

  private updateGrid(html: string): void {
    if (!this.grid) return;

    this.grid.innerHTML = html;
    this.querySelector<ProductCarousel>('product-carousel')?.refresh();
  }

  private async swapGrid(html: string, animate: boolean): Promise<void> {
    if (!this.grid) return;

    if (!html.trim()) {
      if (animate) {
        await this.fadeOutGrid();
      }

      this.updateGrid('');
      gsap.set(this.grid, { autoAlpha: 0, visibility: 'visible' });
      return;
    }

    if (animate) {
      await this.fadeOutGrid();
    }

    this.updateGrid(html);

    if (animate) {
      this.fadeInGrid();
    } else {
      gsap.set(this.grid, { autoAlpha: 1, visibility: 'visible' });
    }
  }

  private async showFallback(animate: boolean): Promise<void> {
    await this.swapGrid(this.fallbackHtml, animate);
  }

  private async refreshRecommendations(): Promise<void> {
    const itemCount = this.getItemCount();
    const sources = this.getSourceProducts();
    const shouldAnimate = this.lastSourceIdsKey !== '';

    if (itemCount === 0) {
      await this.showFallback(shouldAnimate);
      this.lastSourceIdsKey = '';
      return;
    }

    if (sources.length === 0) {
      await this.showFallback(shouldAnimate);
      this.lastSourceIdsKey = '';
      return;
    }

    const sourceIdsKey = this.getSourceIdsKey(sources);
    if (sourceIdsKey === this.lastSourceIdsKey) {
      return;
    }

    if (!this.recommendationsUrl || !this.grid) {
      return;
    }

    const generation = ++this.loadGeneration;

    try {
      if (generation !== this.loadGeneration) return;

      const merged = await this.fetchAndMergeRecommendations(sources);
      if (generation !== this.loadGeneration) return;

      if (!merged.length) {
        await this.swapGrid(this.fallbackHtml, true);
        this.lastSourceIdsKey = sourceIdsKey;
        return;
      }

      const cards = await Promise.all(merged.map((product) => this.fetchProductCard(product.handle)));
      if (generation !== this.loadGeneration) return;

      const html = cards.filter((card): card is string => !!card).join('');

      if (!html) {
        await this.swapGrid(this.fallbackHtml, true);
        this.lastSourceIdsKey = sourceIdsKey;
        return;
      }

      await this.swapGrid(html, true);
      this.lastSourceIdsKey = sourceIdsKey;
    } catch (error) {
      console.error(error);
      if (generation !== this.loadGeneration) return;

      await this.swapGrid(this.fallbackHtml, true);
    }
  }

  private async fetchAndMergeRecommendations(sources: CartProductRef[]): Promise<ScoredProduct[]> {
    const cartIds = new Set(sources.map((product) => product.id));
    const excludeHandles = this.excludeHandles;
    const scores = new Map<number, ScoredProduct>();

    const results = await Promise.all(
      sources.map(async (source) => {
        try {
          return await this.fetchRecommendationsForProduct(source.id);
        } catch (error) {
          console.error(error);
          return [] as RecommendationProduct[];
        }
      }),
    );

    for (const products of results) {
      for (const product of products) {
        if (!product.id || !product.handle) continue;
        if (cartIds.has(product.id)) continue;
        if (excludeHandles.has(product.handle)) continue;
        if (product.available === false) continue;

        const existing = scores.get(product.id);
        if (existing) {
          existing.score += 1;
          continue;
        }

        scores.set(product.id, {
          id: product.id,
          handle: product.handle,
          score: 1,
        });
      }
    }

    return [...scores.values()].sort((a, b) => b.score - a.score).slice(0, FINAL_LIMIT);
  }

  private buildRecommendationsUrl(productId: number): URL {
    const url = new URL(this.recommendationsUrl!, window.location.origin);

    if (!url.pathname.endsWith('.json')) {
      url.pathname = `${url.pathname.replace(/\/$/, '')}.json`;
    }

    url.searchParams.set('product_id', String(productId));
    url.searchParams.set('limit', String(PER_PRODUCT_LIMIT));
    url.searchParams.set('intent', INTENT);

    return url;
  }

  private async fetchRecommendationsForProduct(productId: number): Promise<RecommendationProduct[]> {
    const response = await fetch(this.buildRecommendationsUrl(productId).toString());

    if (!response.ok) {
      throw new Error(`Recommendations fetch failed (${response.status})`);
    }

    const data = (await response.json()) as RecommendationsResponse;

    return data.products ?? [];
  }

  private async fetchProductCard(handle: string): Promise<string | null> {
    if (!this.itemTemplate) return null;

    // @ts-expect-error Shopify is injected on the storefront
    const productUrl = new URL(`${window.Shopify.routes.root}products/${handle}`, window.location.origin);
    const sectionHtml = await getSectionHTML(this.productCardSectionId, productUrl, false);
    const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const card = parsed.querySelector('horizontal-product-card');

    console.log('fixed');

    if (!card) return null;

    const outputFragment = this.itemTemplate.content.cloneNode(true) as DocumentFragment;
    const output = outputFragment.firstElementChild;

    if (!output) return null;

    output.appendChild(card);
    return output.outerHTML.trim();
  }
}

customElements.define('cart-recommended-products', CartRecommendedProducts);
